// COM-638: frontend-side mirror of backend/src/lib/feedRequest.ts's
// `normalizeFeedRequest` (and, since review round 2, `normalizeFeedRequestId`
// below). Two independent callers need the exact same clamp/whitelist rules,
// so this is shared between them rather than duplicated:
//
//   - `SourcesContext#fetchArticlesFeed` normalizes its own params before
//     using them for the empty-result ("no socket yet") fallback shape.
//     Correlating replies to requests is handled separately, by an explicit
//     `requestId` (see `normalizeFeedRequestId` below) - NOT by offset. An
//     earlier version of this file matched replies by normalized offset
//     alone; that broke under a real race (`getArticlesFeed` is an async
//     handler with no serialization between concurrent calls on one socket,
//     so two same-offset requests - e.g. an initial load immediately
//     followed by a search reset - can complete out of send order under DB
//     latency), which offset/limit/sortBy alone can't distinguish.
//   - `mockSocket.ts`'s `getArticlesFeed` case runs the same clamps, so an
//     out-of-range `limit`/`offset` in e2e behaves like it would against the
//     real backend instead of leaking `Array.prototype.slice`'s raw
//     behaviour (e.g. a negative offset silently reading from the end, or an
//     oversized limit returning every row), and echoes `requestId` back with
//     the same cap semantics as the real backend.
//
// Kept deliberately independent of the backend module (different package,
// no shared build step) - if the backend's normalization rules change, this
// file needs a matching edit.
//
// COM-639 Task 2: this mirror now ALSO covers `sourceIds`/`dateFrom`/
// `dateTo` - the backend added them in Task 1 (08b6cf3..a5f3d94) but this
// file deliberately lagged behind until the filter UI that produces them
// existed. `normalizeSourceIds`/`normalizeDate` below are copied from
// backend/src/lib/feedRequest.ts verbatim (same constants, same garbage-only-
// array-collapses-to-null rule, same real-calendar-date check, same inverted-
// range swap) so `articlesFeedMock.ts`'s `buildArticlesFeedPage` sees exactly
// the same shapes `DatabaseService#getArticlesFeed` would build SQL from.

export type FeedSortBy = 'date' | 'title';
export type FeedSortDir = 'asc' | 'desc';

export interface FeedRequestParams {
  limit?: number;
  offset?: number;
  sortBy?: FeedSortBy;
  sortDir?: FeedSortDir;
  search?: string;
  // COM-639: mirrors backend `FeedRequest.sourceIds`/`dateFrom`/`dateTo` -
  // see `NormalizedFeedRequest` below for the null/`[]` semantics these
  // normalize into.
  sourceIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export interface NormalizedFeedRequest {
  limit: number;
  offset: number;
  sortBy: FeedSortBy;
  sortDir: FeedSortDir;
  search: string | null;
  // COM-639: `null` = absent/invalid = no filter applied. `[]` is a distinct,
  // deliberately preserved value meaning "the user deselected every source" -
  // `articlesFeedMock.ts`'s `buildArticlesFeedPage` only filters rows down to
  // an allowed-source set when this is non-null, and an empty array there
  // naturally matches zero rows (mirrors backend `dbService.ts`'s
  // `buildFeedWhereClause` / `= ANY('{}')`). Collapsing `[]` to `null` here
  // would silently turn "show nothing" into "show everything".
  sourceIds: string[] | null;
  // COM-639: exact `YYYY-MM-DD` strings (or null), validated against the
  // real calendar (rejects e.g. `2026-02-31`) - see `normalizeDate` below.
  dateFrom: string | null;
  dateTo: string | null;
}

const DEFAULT_LIMIT = 20;
const MIN_LIMIT = 1;
const MAX_LIMIT = 50;
const DEFAULT_OFFSET = 0;
const DEFAULT_SORT_BY: FeedSortBy = 'date';
const DEFAULT_SORT_DIR: FeedSortDir = 'desc';
const SEARCH_MAX_LENGTH = 200;
const SOURCE_ID_MAX_LENGTH = 64;
// COM-639 fix wave (2026-08-18, Artur): mirrors backend/src/lib/feedRequest.ts's
// own comment on this constant - deliberately unreachable in practice, since
// `SourcesService#addSource` (backend/src/services/sourcesService.ts)
// enforces `MAX_SOURCES_PER_PROJECT` (same value, 100) at source-creation
// time. Kept as a defense-in-depth SQL-safety guard on the backend's own
// query builder, not the primary control.
const SOURCE_IDS_MAX_COUNT = 100;
const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

const SORT_BY_VALUES: FeedSortBy[] = ['date', 'title'];
const SORT_DIR_VALUES: FeedSortDir[] = ['asc', 'desc'];

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  const floored = Math.floor(value);
  return Math.min(max, Math.max(min, floored));
}

function normalizeSortBy(value: unknown): FeedSortBy {
  return SORT_BY_VALUES.includes(value as FeedSortBy) ? (value as FeedSortBy) : DEFAULT_SORT_BY;
}

function normalizeSortDir(value: unknown): FeedSortDir {
  return SORT_DIR_VALUES.includes(value as FeedSortDir) ? (value as FeedSortDir) : DEFAULT_SORT_DIR;
}

function normalizeSearch(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed.slice(0, SEARCH_MAX_LENGTH);
}

// COM-639: mirrors backend `normalizeSourceIds` exactly - non-array/absent ->
// null (no filter, distinct from `[]`); a present array survives even when
// empty (the caller's INTENTIONAL "deselect every source" signal). Non-string
// entries are dropped rather than coerced; each surviving entry is capped at
// `SOURCE_ID_MAX_LENGTH` and the list at `SOURCE_IDS_MAX_COUNT`.
//
// A NON-EMPTY input array whose entries are ALL garbage (e.g. `[123, 456]`,
// `[null]`) also normalizes to `null`, not `[]` - filtering garbage out and
// then treating "nothing survived" the same as "the array started empty"
// would silently turn a malformed payload into "show nothing", the same
// failure mode `[]` is supposed to mean only when the user actually asked
// for it. `[]` therefore reaches this function's return only from a
// genuinely empty input array.
function normalizeSourceIds(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  if (value.length === 0) {
    return [];
  }

  const normalized = value
    .filter((entry): entry is string => typeof entry === 'string')
    .slice(0, SOURCE_IDS_MAX_COUNT)
    .map((entry) => entry.slice(0, SOURCE_ID_MAX_LENGTH));

  return normalized.length === 0 ? null : normalized;
}

// COM-639: mirrors backend `normalizeDate` exactly - exact `YYYY-MM-DD`
// shape plus a real-calendar-date check via a `Date.UTC` round-trip, so
// `2026-02-31` (valid shape, no such day) normalizes to null instead of
// silently rolling over to March 3 the way `new Date('2026-02-31')` would.
// Anything that isn't a string, doesn't match the shape, or isn't a real
// date normalizes to null.
function normalizeDate(value: unknown): string | null {
  if (typeof value !== 'string' || !DATE_FORMAT.test(value)) {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  const isRealDate =
    parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;

  return isRealDate ? value : null;
}

export function normalizeFeedRequest(raw: unknown): NormalizedFeedRequest {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;

  let dateFrom = normalizeDate(source.dateFrom);
  let dateTo = normalizeDate(source.dateTo);

  // Both bounds valid but reversed: swap rather than reject, so a
  // fat-fingered "to" before "from" still yields a sensible window instead
  // of an empty/confusing result. Mirrors backend `normalizeFeedRequest`.
  if (dateFrom !== null && dateTo !== null && dateFrom > dateTo) {
    [dateFrom, dateTo] = [dateTo, dateFrom];
  }

  return {
    limit: clamp(source.limit, MIN_LIMIT, MAX_LIMIT, DEFAULT_LIMIT),
    offset: clamp(source.offset, 0, Number.MAX_SAFE_INTEGER, DEFAULT_OFFSET),
    sortBy: normalizeSortBy(source.sortBy),
    sortDir: normalizeSortDir(source.sortDir),
    search: normalizeSearch(source.search),
    sourceIds: normalizeSourceIds(source.sourceIds),
    dateFrom,
    dateTo,
  };
}

const REQUEST_ID_MAX_LENGTH = 64;

// Mirrors backend/src/lib/feedRequest.ts's `normalizeFeedRequestId` exactly.
// `mockSocket.ts` runs the raw `getArticlesFeed` payload's `requestId`
// through this before echoing it in `articlesFeedLoaded`, so an oversized
// or missing id behaves the same way in e2e as it would against the real
// backend. `requestId` is opaque transport correlation - deliberately not
// part of `NormalizedFeedRequest`/`normalizeFeedRequest` above, the same way
// the backend keeps it out of `FeedRequest`.
export function normalizeFeedRequestId(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }
  return value.slice(0, REQUEST_ID_MAX_LENGTH);
}
